import { Eyebrow, Pill } from '@/components/ui';
import { CheckIcon, CloseIcon } from '@/lib/icons';

const ROWS = [
  {
    feature: 'AI coach / assistance',
    them: 'Absent — no AI anywhere in the product',
    us: 'Our core: the weekly review, the evaluation matrix, prescriptions, and roster-wide risk flags',
  },
  {
    feature: 'Booking & availability',
    them: 'Not part of the product',
    us: 'Slots, individual vs shared, and capacity limits',
  },
  {
    feature: 'Payments & revenue',
    them: 'Not part of the product',
    us: 'Who has paid, who is due, revenue by period and coach',
  },
  {
    feature: 'Lifter-side identity',
    them: 'The client lives inside the coach’s tool',
    us: 'The client has a full lifter product of their own — log, programme, goals, AI coach — and can bring their coach to it, or be brought in by one',
  },
  {
    feature: 'Calendar / schedule',
    them: 'No schedule view',
    us: 'The coach’s week and their clients’ sessions',
  },
];

const THEY_SHIP = [
  'A client-management tool and chat with notifications',
  'Tracking of a client’s workouts for one-to-one sessions',
  'Add or remove an exercise on the go',
  'Weights, reps and duration tracking',
  'A rest timer and exercise replacement',
  'Set types marked warm-up, normal, drop set, failure and superset',
];

/**
 * The honest benchmark against the nearest competitor (platform plan §3b). We
 * state where we differ and credit what they ship — worth watching, worth
 * beating, not worth copying. The competitor is deliberately **unnamed**: the
 * comparison is about the shape of the products, not about picking a fight with
 * a brand, and naming them would date this section the moment they ship.
 */
export function CrmComparison() {
  return (
    <section id="crm-compare" className="border-b border-hairline bg-page py-20">
      <div className="mx-auto max-w-[1200px] 2xl:max-w-[1440px] min-[1920px]:max-w-[1720px] px-4 sm:px-6">
        <div className="mb-10 flex flex-col gap-4">
          <Eyebrow>The benchmark</Eyebrow>
          <h2 className="font-display text-h2 font-black uppercase leading-tight tracking-[-0.01em] text-ink md:text-display-2">
            How we compare with the competition.
          </h2>
          <p className="max-w-[80ch] text-body-lg text-ink-muted">
            The nearest competitor is the coaching arm of a well-established lifting tracker. They are worth
            watching and worth beating — not worth copying. Here is where the two products differ.
          </p>
        </div>

        <div className="grid gap-6 lg:grid-cols-[1.6fr_1fr]">
          <div className="min-w-0 overflow-x-auto rounded-card border border-hairline bg-surface shadow-card">
            <table className="w-full min-w-[560px] border-separate border-spacing-0 text-left">
              <caption className="sr-only">Lift2Fit CRM compared with the competition</caption>
              <thead>
                <tr>
                  {['', 'The competition', 'Lift2Fit'].map((heading, index) => (
                    <th
                      key={heading || 'feature'}
                      scope="col"
                      className="border-b border-hairline px-5 py-3 text-[10px] font-bold uppercase tracking-[0.1em] text-ink-muted"
                    >
                      {index === 2 ? (
                        <span className="inline-flex items-center gap-2 text-ink">
                          Lift2Fit <Pill tone="accent">CRM</Pill>
                        </span>
                      ) : (
                        heading
                      )}
                    </th>
                  ))}
                </tr>
              </thead>
              <tbody className="align-top text-[13px]">
                {ROWS.map((row) => (
                  <tr key={row.feature}>
                    <th scope="row" className="border-b border-hairline px-5 py-4 text-left font-bold text-ink">
                      {row.feature}
                    </th>
                    <td className="border-b border-hairline px-5 py-4 text-ink-muted">
                      <span className="flex items-start gap-2">
                        <CloseIcon className="mt-[2px] shrink-0 text-sm text-ink-muted" />
                        {row.them}
                      </span>
                    </td>
                    <td className="border-b border-hairline px-5 py-4 font-medium text-ink">
                      <span className="flex items-start gap-2">
                        <CheckIcon className="mt-[2px] shrink-0 text-sm text-accent" />
                        {row.us}
                      </span>
                    </td>
                  </tr>
                ))}
              </tbody>
            </table>
          </div>

          <div className="flex flex-col gap-4 rounded-card border border-hairline bg-surface p-6 shadow-card">
            <h3 className="font-display text-[17px] font-black uppercase tracking-[-0.01em] text-ink">
              What the competition does ship
            </h3>
            <ul className="flex flex-col gap-2.5">
              {THEY_SHIP.map((item) => (
                <li key={item} className="flex items-start gap-2.5 text-[13px] leading-snug text-ink-muted">
                  <CheckIcon className="mt-[2px] shrink-0 text-sm text-ink-muted" />
                  {item}
                </li>
              ))}
            </ul>
            <p className="border-t border-hairline pt-4 text-[12px] leading-relaxed text-ink-muted">
              Their tracker app is free and the coaching platform is sold separately. Tracking and chat are
              table stakes, not a differentiator: what they do not have is AI assistance, booking or payments —
              and that is where we win. Pricing and features move, so we re-benchmark before every pricing
              decision.
            </p>
          </div>
        </div>
      </div>
    </section>
  );
}
